import { useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { MessageSquareWarning, Menu } from 'lucide-react'

import { useAuth } from '@/auth/AuthContext'
import { ROLE_LABELS } from '@/auth/permissions'
import { FeedbackModal } from '@/components/feedback/FeedbackModal'
import { UpdatePrompt } from '@/components/update/UpdatePrompt'
import { Avatar } from '@/components/ui/Avatar'
import { IconButton } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import { useOrientationPolicy } from '@/hooks/useOrientation'
import { SideNav } from './SideNav'

export function AppLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // Pion wszędzie poza grafikiem — sterowane z jednego miejsca, bo blokada
  // orientacji jest stanem całego okna, nie pojedynczego ekranu.
  useOrientationPolicy(pathname)
  const { notifyError } = useToast()
  const [isNavOpen, setIsNavOpen] = useState(false)
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/logowanie', { replace: true })
    } catch {
      notifyError('Nie udało się wylogować. Spróbuj ponownie.')
    }
  }

  return (
    <div className="min-h-dvh bg-background">
      <SideNav isOpen={isNavOpen} onClose={() => setIsNavOpen(false)} onLogout={handleLogout} />

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-outline/10 bg-surface px-4 sm:px-8 telefon-poziomo:h-12">
          <IconButton
            label="Otwórz menu"
            icon={<Menu size={20} />}
            onClick={() => setIsNavOpen(true)}
            className="lg:hidden"
          />

          {/* Zgłoszenie wysyła się z ekranu, na którym coś nie zadziałało —
              dlatego przycisk stoi w pasku, a nie w osobnej zakładce. Tylko
              stąd da się dołączyć kontekst, w którym błąd wystąpił. */}
          <IconButton
            label="Zgłoś błąd lub sugestię"
            icon={<MessageSquareWarning size={20} />}
            onClick={() => setIsFeedbackOpen(true)}
            className="ml-auto"
          />

          <div className="flex items-center gap-4">
            <div className="hidden text-right sm:block">
              <p className="text-xs font-bold text-primary">{user?.full_name}</p>
              <p className="text-[10px] text-on-surface-variant">
                {user ? ROLE_LABELS[user.role] : ''}
              </p>
            </div>
            <Avatar name={user?.full_name ?? '?'} />
          </div>
        </header>

        <main className="mx-auto max-w-content px-4 py-6 sm:px-8 sm:py-8 telefon-poziomo:py-3">
          <Outlet />
        </main>
      </div>

      <FeedbackModal open={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
      <UpdatePrompt />
    </div>
  )
}

interface PageHeaderProps {
  title: string
  description?: string
  action?: React.ReactNode
  breadcrumb?: string[]
}

/** Nagłówek strony — tytuł serifem, jak w szablonach graficznych. */
export function PageHeader({ title, description, action, breadcrumb }: PageHeaderProps) {
  return (
    // Telefon w poziomie: nagłówek zjadał cały ekran i siatka grafiku
    // zaczynała się poniżej krawędzi (zmierzone na 844×390 — zostawało na nią
    // około 40 px). Ścieżka i opis znikają, tytuł maleje, marginesy się kurczą;
    // treść, po którą ktoś obrócił telefon, wchodzi w kadr.
    <header className="mb-8 flex flex-wrap items-end justify-between gap-4 telefon-poziomo:mb-3">
      <div>
        {breadcrumb && breadcrumb.length > 0 && (
          <nav aria-label="Ścieżka nawigacji" className="mb-2 telefon-poziomo:hidden">
            <ol className="flex flex-wrap items-center gap-2 text-xs font-semibold text-on-surface-variant">
              {breadcrumb.map((crumb, index) => (
                <li key={crumb} className="flex items-center gap-2">
                  {index > 0 && <span aria-hidden="true">›</span>}
                  <span className={index === breadcrumb.length - 1 ? 'text-primary' : undefined}>
                    {crumb}
                  </span>
                </li>
              ))}
            </ol>
          </nav>
        )}
        <h1 className="font-headline text-3xl font-bold text-secondary sm:text-4xl telefon-poziomo:text-2xl">
          {title}
        </h1>
        {description && (
          <p className="mt-2 text-on-surface-variant telefon-poziomo:hidden">{description}</p>
        )}
      </div>
      {action}
    </header>
  )
}
